/**
    * @description      : 
    * @author           : USER
    * @group            : 
    * @created          : 25/11/2025 - 20:47:34
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 25/11/2025
    * - Author          : USER
    * - Modification    : 
**/
import React, { useState, useRef, useEffect } from 'react';
import { waitForClerkToken } from '../../services/clerkToken';
import './VoiceInput.css';

const getApiBaseUrl = () => {
  const configuredUrl = (process.env.REACT_APP_API_URL || '').trim();

  if (configuredUrl && configuredUrl !== 'undefined') {
    return configuredUrl.replace(/\/$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return 'http://localhost:8000';
};

/**
 * 🎤 VoiceInput Component
 *
 * Beautiful voice recording interface with real-time waveform visualization.
 * Supports 95+ languages including English, Swahili, Somali, Arabic, and more!
 *
 * Features:
 * - Real-time audio recording
 * - Visual feedback during recording
 * - Auto-transcription using Whisper AI
 * - Multilingual support
 * - Mobile-friendly
 */
const VoiceInput = ({ onTranscriptionComplete, language = 'en', contextType = 'healthcare' }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const ensureVoiceConsent = () => {
    try {
      if (localStorage.getItem('tulizaVoiceConsentAccepted') === 'true') {
        return true;
      }
    } catch {}

    const accepted = window.confirm(
      'Voice input will be transcribed. Chat messages are saved automatically with your conversation, so do not share passport numbers, Home Office references, or sensitive IDs. Continue?'
    );

    if (accepted) {
      try {
        localStorage.setItem('tulizaVoiceConsentAccepted', 'true');
      } catch {}
    }

    return accepted;
  };

  const startRecording = async () => {
    try {
      setError(null);

      if (!ensureVoiceConsent()) {
        return;
      }

      // Request microphone access with BEST QUALITY settings for accuracy
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,  // High quality sampling
          channelCount: 1,     // Mono for better speech recognition
          sampleSize: 16,      // 16-bit audio quality
        }
      });

      streamRef.current = stream;

      // Determine the best MIME type
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        await transcribeAudio(audioBlob, mimeType);

        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      console.log('🎤 Recording started');
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      console.log('🎤 Recording stopped');
    }
  };

  const transcribeAudio = async (audioBlob, mimeType) => {
    try {
      setIsTranscribing(true);
      setError(null);

      if (!audioBlob || audioBlob.size === 0) {
        throw new Error('Audio file is required');
      }

      const formData = new FormData();
      const extension = mimeType === 'audio/webm' ? 'webm' : 'mp4';
      formData.append('audio', audioBlob, `recording.${extension}`);

      if (language && language !== 'auto') {
        formData.append('language', language);
      }

      formData.append('context_type', contextType);

      console.log(`🚀 Sending audio for transcription (${(audioBlob.size / 1024).toFixed(1)}KB)`);

      const API_URL = getApiBaseUrl();

      // Build headers with auth token if available
      const headers = {};
      try {
        const token = await waitForClerkToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (e) {
        // Continue without auth
      }

      const response = await fetch(`${API_URL}/api/chatbot/chats/transcribe_voice/`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }

      if (data.success) {
        console.log(`✅ Transcription successful: "${data.text}"`);
        onTranscriptionComplete(data.text);
      } else {
        throw new Error(data.error || 'Transcription failed');
      }
    } catch (err) {
      console.error('Transcription error:', err);
      setError(err.message || 'Failed to transcribe audio. Please try again.');
    } finally {
      setIsTranscribing(false);
      setRecordingTime(0);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="voice-input-container">
      {!isRecording && !isTranscribing ? (
        // Mic button (ready to record)
        <button
          className="voice-button"
          onClick={startRecording}
          title="Click to record voice message"
          aria-label="Record voice message"
        >
          <svg
            className="mic-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        </button>
      ) : isRecording ? (
        // Recording in progress
        <div className="recording-controls">
          <button
            className="voice-button recording"
            onClick={stopRecording}
            title="Click to stop recording"
            aria-label="Stop recording"
          >
            <div className="recording-indicator">
              <div className="recording-pulse"></div>
              <svg
                className="mic-icon recording-icon"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" stroke="currentColor" strokeWidth="2" />
                <line x1="8" y1="23" x2="16" y2="23" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
          </button>
          <span className="recording-time">{formatTime(recordingTime)}</span>
          <span className="recording-label">Recording...</span>
        </div>
      ) : (
        // Transcribing
        <div className="transcribing-container">
          <div className="transcribing-spinner"></div>
          <span className="transcribing-label">Transcribing...</span>
        </div>
      )}

      {error && (
        <div className="voice-error">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default VoiceInput;
